import React, { useState, useEffect, useCallback } from "react";
import { Grid, CardHeader, CardContent, Box, TextField, FormControlLabel, Switch, MenuItem } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../../layouts/CardLayout";
import errorHandler from "../../../utils/errorHandler";
import { locationServices } from "../../../services/AllServices";

function AddEditState() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [countries, setCountries] = useState([]);

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await locationServices.getByIdState({ stateId: id , type:"dropdown"});
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await locationServices.getCountry();
      setCountries(response?.data?.data || []);
    } catch (error) {
      errorHandler(error);
    }
  }, []);

  useEffect(() => {
    fetchTaskData();
    fetchCountries();
  }, [fetchTaskData, fetchCountries]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      countryId: data?.country?._id || "",
      name: data?.name || "",
      status: data?.status ?? true,
    },

    validate: values => {
      const errors = {};
      if (!values.name.trim()) {
        errors.name = "This field is required";
      }
      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          stateId: id || undefined,
          countryId: values.countryId,
          name: values.name.trim(),
          status: values.status,
        };

        const response = id
          ? await locationServices.editState(payload)
          : await locationServices.addState(payload);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue } = formik;

  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} State`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Select Country"
                    name="countryId"
                    value={values.countryId}
                    onChange={handleChange}
                    error={Boolean(touched.countryId && errors.countryId)}
                    helperText={touched.countryId && errors.countryId}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country._id} value={country._id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="State Name"
                    required
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.status}
                        onChange={(e) => setFieldValue("status", e.target.checked)}
                        name="status"
                        color="primary"
                      />
                    }
                    label="Status (Active)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} State
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditState;